import React, { useRef, useEffect, useMemo, useImperativeHandle, forwardRef, useCallback } from 'react';
import * as d3 from 'd3';

/**
 * PathwayCanvas - SVG container with zoom/pan behavior
 *
 * Minimal component that just handles:
 * - SVG setup
 * - Zoom/pan behavior
 * - Fit to content bounds on first load; later content changes re-fit only
 *   when the caller asks with autoFitIdleTimeout, and then only after an idle period
 * - Passing transform to children
 * - Programmatic panning (for modal positioning)
 */
const PathwayCanvas = forwardRef(function PathwayCanvas({
  width,
  height,
  onZoomChange,
  contentBounds, // { minX, minY, maxX, maxY } - bounds of all content
  cardWidth = 360,
  cardHeight = 90,
  padding = 50, // Padding around content when fitting
  // Re-fit after content changes once the user has been idle this long. Off
  // by default: the impact map grows under people's hands, and zooming out on
  // every new node took their place away (Kuutio 10, #1431).
  autoFitIdleTimeout = null,
  // Tapping empty canvas puts the map back to normal. Panning must not: a drag
  // ends in a click event too, so the distance decides.
  onBackgroundClick,
  children
}, ref) {
  const svgRef = useRef();
  const containerRef = useRef();
  const zoomRef = useRef(null);
  const hasInitializedRef = useRef(false);
  const lastInteractionRef = useRef(Date.now()); // Track last user zoom/pan
  const pendingFitRef = useRef(null); // Pending auto-fit timeout

  // Calculate optimal transform to fit all content
  // MUST be defined before useImperativeHandle since fitAll() uses it
  const fitTransform = useMemo(() => {
    if (!contentBounds || !width || !height) {
      // Default fallback: centered, zoomed out
      return { k: 0.4, x: width / 2, y: 100, minScale: 0.05 };
    }

    const { minX, minY, maxX, maxY } = contentBounds;

    // Add card dimensions to bounds (positions are top-left of cards)
    const contentWidth = (maxX - minX) + cardWidth;
    const contentHeight = (maxY - minY) + cardHeight;

    // Calculate scale to fit content with padding
    const availableWidth = width - (padding * 2);
    const availableHeight = height - (padding * 2);

    const scaleX = availableWidth / contentWidth;
    const scaleY = availableHeight / contentHeight;

    // Use the smaller scale to ensure everything fits
    // No minimum clamp - allow any zoom level needed to fit content
    const fitScale = Math.min(scaleX, scaleY);
    const scale = Math.min(fitScale, 1.5); // Only cap at max, not min

    // Calculate minimum zoom level: allow zooming out slightly more than fit scale
    // This ensures users can always see the entire map
    const minScale = Math.min(fitScale * 0.8, 0.05); // 80% of fit scale or 5%, whichever is smaller

    // Calculate center of content
    const contentCenterX = minX + contentWidth / 2;
    const contentCenterY = minY + contentHeight / 2;

    // Calculate translation to center content in viewport
    const translateX = (width / 2) - (contentCenterX * scale);
    const translateY = (height / 2) - (contentCenterY * scale);

    console.log('📐 Fit transform calculated:', {
      contentBounds,
      contentSize: { contentWidth, contentHeight },
      viewportSize: { width, height },
      scale: scale.toFixed(3),
      minScale: minScale.toFixed(4),
      translate: { x: translateX.toFixed(0), y: translateY.toFixed(0) }
    });

    return { k: scale, x: translateX, y: translateY, minScale };
  }, [contentBounds, width, height, cardWidth, cardHeight, padding]);

  // Expose methods to parent via ref
  useImperativeHandle(ref, () => ({
    /**
     * Frame the current content now, ignoring the idle rule.
     *
     * Auto-fit deliberately waits for a quiet moment so it cannot yank the map
     * away from somebody mid-sentence. Entering the focused view is the
     * exception: the content has just been replaced by two cards, and leaving
     * them in the corner at the old zoom is not a view of anything.
     */
    fitNow: (animate = true) => {
      if (!svgRef.current || !zoomRef.current) return;
      const svg = d3.select(svgRef.current);
      const t = d3.zoomIdentity.translate(fitTransform.x, fitTransform.y).scale(fitTransform.k);
      lastInteractionRef.current = Date.now();
      if (animate) svg.transition().duration(320).call(zoomRef.current.transform, t);
      else svg.call(zoomRef.current.transform, t);
    },

    // Pan view so a specific SVG point appears at a screen position
    panToPoint: (svgX, svgY, screenX, screenY, animate = true) => {
      if (!svgRef.current || !zoomRef.current) return;

      const svg = d3.select(svgRef.current);
      const currentTransform = d3.zoomTransform(svgRef.current);

      // Calculate new translation to position svgX,svgY at screenX,screenY
      const newX = screenX - svgX * currentTransform.k;
      const newY = screenY - svgY * currentTransform.k;

      const newTransform = d3.zoomIdentity
        .translate(newX, newY)
        .scale(currentTransform.k);

      if (animate) {
        svg.transition()
          .duration(300)
          .call(zoomRef.current.transform, newTransform);
      } else {
        svg.call(zoomRef.current.transform, newTransform);
      }
    },

    // Get current transform
    getTransform: () => {
      if (!svgRef.current) return null;
      return d3.zoomTransform(svgRef.current);
    },

    // Pan by delta (screen pixels)
    panBy: (deltaX, deltaY, animate = true) => {
      if (!svgRef.current || !zoomRef.current) return;

      const svg = d3.select(svgRef.current);
      const currentTransform = d3.zoomTransform(svgRef.current);

      const newTransform = d3.zoomIdentity
        .translate(currentTransform.x + deltaX, currentTransform.y + deltaY)
        .scale(currentTransform.k);

      if (animate) {
        svg.transition()
          .duration(300)
          .call(zoomRef.current.transform, newTransform);
      } else {
        svg.call(zoomRef.current.transform, newTransform);
      }
    },

    // Center and zoom to a specific SVG coordinate (node center)
    // scale: 1.0 = 100% zoom (good for iPad reading)
    zoomToPoint: (svgX, svgY, scale = 1.0, animate = true) => {
      if (!svgRef.current || !zoomRef.current) return;

      const svg = d3.select(svgRef.current);

      // Calculate translation to center the point on screen
      const centerX = width / 2;
      const centerY = height / 2;
      const translateX = centerX - svgX * scale;
      const translateY = centerY - svgY * scale;

      const newTransform = d3.zoomIdentity
        .translate(translateX, translateY)
        .scale(scale);

      if (animate) {
        svg.transition()
          .duration(500)
          .ease(d3.easeCubicOut)
          .call(zoomRef.current.transform, newTransform);
      } else {
        svg.call(zoomRef.current.transform, newTransform);
      }
    },

    // Fit all content in view (zoom to show everything)
    fitAll: (animate = true) => {
      if (!svgRef.current || !zoomRef.current) return;

      const svg = d3.select(svgRef.current);
      const transform = d3.zoomIdentity
        .translate(fitTransform.x, fitTransform.y)
        .scale(fitTransform.k);

      if (animate) {
        svg.transition()
          .duration(500)
          .ease(d3.easeCubicOut)
          .call(zoomRef.current.transform, transform);
      } else {
        svg.call(zoomRef.current.transform, transform);
      }
    },

    /** Treat as fresh user activity — defers auto-fit after layout/contentBounds changes (e.g. node delete). */
    markInteraction: () => {
      lastInteractionRef.current = Date.now();
      if (pendingFitRef.current) {
        clearTimeout(pendingFitRef.current);
        pendingFitRef.current = null;
      }
    },
  }), [width, height, fitTransform]);

  // Initialize zoom behavior
  useEffect(() => {
    if (!svgRef.current) return;

    const svg = d3.select(svgRef.current);
    const container = d3.select(containerRef.current);

    // Use dynamic min scale from fitTransform, fallback to 0.05
    const minZoom = fitTransform.minScale || 0.05;

    // While a gesture is in flight the svg carries `is-zooming`, and the
    // stylesheet below switches every CSS transition off under it. Hundreds
    // of nodes and arrows each animate their own style; letting them all
    // re-evaluate a transition on every wheel tick was most of the stutter.
    let zoomEndTimer = null;
    const zoom = d3.zoom()
      .scaleExtent([minZoom, 4]) // Dynamic min based on content size, max 400%
      .on('start', () => { svg.classed('is-zooming', true); })
      .on('end', () => {
        // d3 fires end per gesture; a wheel is many gestures, so settle first.
        clearTimeout(zoomEndTimer);
        zoomEndTimer = setTimeout(() => svg.classed('is-zooming', false), 120);
      })
      .on('zoom', (event) => {
        container.attr('transform', event.transform);
        // Track user interaction to prevent auto-fit from disrupting active users
        if (event.sourceEvent) {
          // Only count as user interaction if triggered by mouse/touch/wheel
          lastInteractionRef.current = Date.now();
        }
        if (onZoomChange) {
          onZoomChange({
            k: event.transform.k,
            x: event.transform.x,
            y: event.transform.y
          });
        }
      });

    svg.call(zoom);
    zoomRef.current = zoom;

    // Store zoom behavior on the SVG element for programmatic access
    svg.node().__zoomBehavior = zoom;

    // Cleanup
    return () => {
      svg.on('.zoom', null);
    };
  }, [onZoomChange, fitTransform.minScale]);

  // Apply fit transform when content bounds change (or on initial load)
  // Respects idle timeout to avoid disrupting users who are actively zooming/panning
  useEffect(() => {
    if (!svgRef.current || !zoomRef.current || !contentBounds) return;

    const svg = d3.select(svgRef.current);

    // Apply the calculated fit transform with smooth animation
    const transform = d3.zoomIdentity
      .translate(fitTransform.x, fitTransform.y)
      .scale(fitTransform.k);

    // First load: apply immediately
    if (!hasInitializedRef.current) {
      svg.call(zoomRef.current.transform, transform);
      hasInitializedRef.current = true;
      if (onZoomChange) {
        onZoomChange({ k: fitTransform.k, x: fitTransform.x, y: fitTransform.y });
      }
      return;
    }

    if (autoFitIdleTimeout == null) return;

    // Subsequent updates: only auto-fit if user has been idle
    // Clear any pending auto-fit
    if (pendingFitRef.current) {
      clearTimeout(pendingFitRef.current);
      pendingFitRef.current = null;
    }

    const timeSinceLastInteraction = Date.now() - lastInteractionRef.current;

    if (timeSinceLastInteraction >= autoFitIdleTimeout) {
      // User has been idle long enough, apply auto-fit now
      svg.transition()
        .duration(300)
        .call(zoomRef.current.transform, transform);
    } else {
      // User was recently active, schedule auto-fit for later
      const remainingIdleTime = autoFitIdleTimeout - timeSinceLastInteraction;
      pendingFitRef.current = setTimeout(() => {
        // Check again if user is still idle
        const currentIdleTime = Date.now() - lastInteractionRef.current;
        if (currentIdleTime >= autoFitIdleTimeout && svgRef.current && zoomRef.current) {
          d3.select(svgRef.current)
            .transition()
            .duration(300)
            .call(zoomRef.current.transform, transform);
        }
        pendingFitRef.current = null;
      }, remainingIdleTime + 100); // Small buffer
    }

    // Cleanup pending timeout on unmount
    return () => {
      if (pendingFitRef.current) {
        clearTimeout(pendingFitRef.current);
        pendingFitRef.current = null;
      }
    };
  }, [fitTransform, contentBounds, autoFitIdleTimeout, onZoomChange]);

  const downRef = useRef(null);

  return (
    <svg
      ref={svgRef}
      width={width}
      height={height}
      className="pathway-canvas"
      onPointerDown={(e) => { downRef.current = { x: e.clientX, y: e.clientY }; }}
      onPointerUp={(e) => {
        const start = downRef.current;
        downRef.current = null;
        if (!onBackgroundClick || !start) return;
        const moved = Math.hypot(e.clientX - start.x, e.clientY - start.y);
        if (moved > 4) return; // that was a pan, not a tap
        onBackgroundClick();
      }}
      style={{
        width: '100%',
        height: '100%',
        background: 'transparent'
      }}
    >
      <style>{'.pathway-canvas.is-zooming * { transition: none !important; }'}</style>
      <g ref={containerRef} className="main-container">
        {children}
      </g>
    </svg>
  );
});

export default PathwayCanvas;
